import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { apiFetch } from '../bizsuite-app';
import { toast } from '../utils/toast';

/**
 * Contract Allocation — Drag-and-Drop Assignment (Spatial Canvas).
 *
 * Mirrors OrgDragDropAssign.jsx, but Column 3 is a hierarchical tree of
 *   Contract  →  Line Item  →  Sub-Item
 * Each of those nodes is a valid drop target. On drop we POST to
 * /api/contract-allocations with the target tuple.
 *
 * Props:
 *   employees      — full employee list
 *   allocations    — existing contract allocations (rows from /api/contract-allocations)
 *   contractTree   — [{ contract fields, line_items: [{ line-item fields, sub_items: [...] }] }]
 *   skillGroups    — [{ value, label }]
 *   skillsets      — [{ value, label }]
 *   empSkillsMap   — { empId: [skillGroupName, ...] }
 *   empSkillsetMap — { empId: [skillsetName, ...] }
 *   onChange       — called after remote save when we want the parent to refetch
 */
export default function ContractDragDropAssign({
  employees = [],
  allocations = [],
  contractTree = [],
  skillGroups = [],
  skillsets = [],
  empSkillsMap = {},
  empSkillsetMap = {},
  onChange = () => {},
}) {
  /* Left rail (employees) filters */
  const [lName, setLName] = useState('');
  const [lDept, setLDept] = useState('');
  const [lGroup, setLGroup] = useState('');
  const [lSkillset, setLSkillset] = useState('');

  /* Right rail (contract tree) filters */
  const [rSearch, setRSearch]     = useState('');      // contract number / title / customer
  const [rStatus, setRStatus]     = useState('Active'); // Active / All
  const [rCustomer, setRCustomer] = useState('');

  /* Drag state + hover feedback */
  const [draggingEmpId, setDraggingEmpId] = useState(null);
  const [hoverTargetKey, setHoverTargetKey] = useState(null);

  /* Expanded state for contract / line-item nodes */
  const [expanded, setExpanded] = useState({}); // { [nodeKey]: true }
  const toggleExpand = (k) => setExpanded(x => ({ ...x, [k]: !x[k] }));

  /* Left rail "Show" filter */
  const [showFilter, setShowFilter] = useState('all'); // 'all' | 'noalloc' | 'alloc'

  /* Local allocations — mirrors prop for optimistic updates */
  const [localAllocs, setLocalAllocs] = useState(allocations || []);
  useEffect(() => { setLocalAllocs(allocations || []); }, [allocations]);

  /* Derived department list */
  const departments = useMemo(() => {
    const seen = new Set();
    employees.forEach(e => { if (e.department_name) seen.add(e.department_name); });
    return [...seen].sort();
  }, [employees]);

  /* Derived customer list (from the tree) */
  const customers = useMemo(() => {
    const seen = new Set();
    contractTree.forEach(c => { if (c.customer_name) seen.add(c.customer_name); });
    return [...seen].sort();
  }, [contractTree]);

  /* Lookup: empId → has any active allocation? */
  const allocByEmp = useMemo(() => {
    const m = {};
    (localAllocs || []).forEach(a => {
      if (!m[a.employee_id]) m[a.employee_id] = [];
      m[a.employee_id].push(a);
    });
    return m;
  }, [localAllocs]);

  const isActive = (e) => {
    const s = String(e.status || 'Active').toUpperCase();
    return s === 'ACTIVE';
  };

  const matchesLeftFilters = useCallback((emp, nameQ, dept, group, skillset) => {
    if (nameQ) {
      const q = nameQ.toLowerCase();
      const full = `${emp.first_name || ''} ${emp.last_name || ''} ${emp.emp_code || ''}`.toLowerCase();
      if (!full.includes(q)) return false;
    }
    if (dept && String(emp.department_name || '') !== dept) return false;
    if (group) {
      const mine = empSkillsMap[emp.id] || [];
      if (!mine.some(g => g.toLowerCase() === group.toLowerCase()) &&
          String(emp.module || '').toLowerCase() !== group.toLowerCase()) return false;
    }
    if (skillset) {
      const mineSs = empSkillsetMap[emp.id] || [];
      if (!mineSs.some(s => s.toLowerCase() === skillset.toLowerCase())) return false;
    }
    return true;
  }, [empSkillsMap, empSkillsetMap]);

  const totalActiveEmployees = useMemo(
    () => employees.filter(isActive).length,
    [employees]
  );

  const leftList = useMemo(() => employees.filter(e => {
    if (!isActive(e)) return false;
    if (!matchesLeftFilters(e, lName, lDept, lGroup, lSkillset)) return false;
    if (showFilter !== 'all') {
      const has = (allocByEmp[e.id] || []).length > 0;
      if (showFilter === 'noalloc' && has) return false;
      if (showFilter === 'alloc'   && !has) return false;
    }
    return true;
  }), [employees, lName, lDept, lGroup, lSkillset, matchesLeftFilters, showFilter, allocByEmp]);

  /* Right tree — filter by search / status / customer */
  const filteredTree = useMemo(() => {
    const q = rSearch.trim().toLowerCase();
    return (contractTree || []).filter(c => {
      if (rStatus && rStatus !== 'All' && String(c.status || '').toLowerCase() !== rStatus.toLowerCase()) return false;
      if (rCustomer && String(c.customer_name || '') !== rCustomer) return false;
      if (q) {
        const hay = `${c.contract_number || ''} ${c.title || ''} ${c.customer_name || ''} ${c.project_name || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [contractTree, rSearch, rStatus, rCustomer]);

  /* ── Connectors (employee → target) ── */
  const connections = useMemo(() => {
    const out = [];
    (localAllocs || []).forEach(a => {
      // Prefer most-specific target that is visible
      const keySub = a.sub_item_id ? `sub:${a.sub_item_id}` : null;
      const keyItem = a.line_item_id ? `item:${a.line_item_id}` : null;
      const keyContract = a.contract_id ? `contract:${a.contract_id}` : null;
      out.push({
        from: a.employee_id,
        toCandidates: [keySub, keyItem, keyContract].filter(Boolean),
        status: a.status,
      });
    });
    return out;
  }, [localAllocs]);

  /* ── Refs & connector redraw ── */
  const canvasRef = useRef(null);
  const svgRef = useRef(null);
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const [repaintTick, setRepaintTick] = useState(0);
  const repaint = useCallback(() => setRepaintTick(t => t + 1), []);

  useEffect(() => {
    window.addEventListener('resize', repaint);
    return () => window.removeEventListener('resize', repaint);
  }, [repaint]);

  useEffect(() => {
    const L = leftRef.current, R = rightRef.current;
    if (L) L.addEventListener('scroll', repaint);
    if (R) R.addEventListener('scroll', repaint);
    return () => {
      if (L) L.removeEventListener('scroll', repaint);
      if (R) R.removeEventListener('scroll', repaint);
    };
  }, [repaint]);

  /* Redraw connectors */
  useEffect(() => {
    const svg = svgRef.current;
    const canvas = canvasRef.current;
    if (!svg || !canvas) return;
    const cr = canvas.getBoundingClientRect();
    svg.setAttribute('viewBox', `0 0 ${cr.width} ${cr.height}`);
    svg.innerHTML = '';
    const ns = 'http://www.w3.org/2000/svg';

    const color = (s) => (String(s).toLowerCase() === 'active' ? '#00E0FF' : '#94A3B8');

    connections.forEach((c) => {
      const l = canvas.querySelector(`[data-role="emp-left"][data-id="${c.from}"]`);
      if (!l) return;
      // Try most-specific → least-specific
      let r = null;
      for (const k of c.toCandidates) {
        const cand = canvas.querySelector(`[data-role="tgt"][data-key="${k}"]`);
        if (cand) { r = cand; break; }
      }
      if (!r) return;
      const lr = l.getBoundingClientRect();
      const rr = r.getBoundingClientRect();
      const x1 = lr.right - cr.left;
      const y1 = lr.top + lr.height / 2 - cr.top;
      const x2 = rr.left  - cr.left;
      const y2 = rr.top + rr.height / 2 - cr.top;
      const midX = (x1 + x2) / 2;

      const path = document.createElementNS(ns, 'path');
      path.setAttribute('d', `M ${x1} ${y1} C ${midX} ${y1}, ${midX} ${y2}, ${x2} ${y2}`);
      path.setAttribute('stroke', color(c.status));
      path.setAttribute('stroke-width', 2);
      path.setAttribute('fill', 'none');
      path.setAttribute('opacity', 0.75);
      path.setAttribute('filter', `drop-shadow(0 0 6px ${color(c.status)})`);
      svg.appendChild(path);

      [ [x1, y1], [x2, y2] ].forEach(([cx, cy]) => {
        const circle = document.createElementNS(ns, 'circle');
        circle.setAttribute('cx', cx);
        circle.setAttribute('cy', cy);
        circle.setAttribute('r', 3.5);
        circle.setAttribute('fill', color(c.status));
        svg.appendChild(circle);
      });
    });
  }, [connections, leftList, filteredTree, expanded, repaintTick]);

  /* ── Drop handling ── */
  async function handleDrop(empId, target) {
    // target = { contract, line_item?, sub_item? }
    const empName = nameOf(empId);
    const targetLabel = describeTarget(target);
    toast.success(`${empName} → ${targetLabel}`);

    // Optimistic
    const prev = localAllocs;
    const localId = '_local_' + Date.now();
    const now = new Date().toISOString();
    const from_date =
      (target.sub_item && target.sub_item.start_date) ||
      (target.line_item && target.line_item.start_date) ||
      target.contract.start_date || null;
    const to_date =
      (target.sub_item && target.sub_item.end_date) ||
      (target.line_item && target.line_item.end_date) ||
      target.contract.end_date || null;

    const optimistic = {
      id: localId,
      employee_id: empId,
      contract_id: target.contract.id,
      line_item_id: target.line_item ? target.line_item.id : null,
      sub_item_id: target.sub_item ? target.sub_item.id : null,
      from_date, to_date,
      allocation_pct: target.sub_item?.allocation_pct || 100,
      status: 'Active',
      created_at: now, updated_at: now,
      // join fields for display
      contract_number: target.contract.contract_number,
      contract_title: target.contract.title,
      customer_name: target.contract.customer_name,
      line_item_name: target.line_item?.item_name,
      sub_item_name: target.sub_item?.sub_item_name,
    };
    setLocalAllocs(prevRows => [...prevRows, optimistic]);

    try {
      const res = await apiFetch('/api/contract-allocations', {
        method: 'POST',
        body: JSON.stringify({
          employee_id: empId,
          contract_id: target.contract.id,
          line_item_id: target.line_item ? target.line_item.id : null,
          sub_item_id: target.sub_item ? target.sub_item.id : null,
          from_date, to_date,
          allocation_pct: optimistic.allocation_pct,
          status: 'Active',
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setLocalAllocs(prev);
        toast.error(`Save failed: ${d.error || 'unknown error'}`);
        return;
      }
      const { id } = await res.json().catch(() => ({}));
      if (id) {
        setLocalAllocs(rows => rows.map(r => r.id === localId ? { ...r, id } : r));
      }
      // parent refetch so the stored row carries the fresh join fields next time
      onChange();
    } catch (e) {
      setLocalAllocs(prev);
      toast.error(`Network error: ${e.message}`);
    }
  }

  async function removeAllocation(allocId) {
    const prev = localAllocs;
    setLocalAllocs(rows => rows.filter(r => r.id !== allocId));
    try {
      const res = await apiFetch(`/api/contract-allocations/${allocId}`, { method: 'DELETE' });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setLocalAllocs(prev);
        toast.error(`Delete failed: ${d.error || 'unknown error'}`);
        return;
      }
      toast.success('Allocation removed');
      onChange();
    } catch (e) {
      setLocalAllocs(prev);
      toast.error(`Network error: ${e.message}`);
    }
  }

  function nameOf(empId) {
    const e = employees.find(x => String(x.id) === String(empId));
    return e ? `${e.first_name || ''} ${e.last_name || ''}`.trim() : empId;
  }

  function describeTarget(t) {
    if (t.sub_item)  return `${t.contract.contract_number || t.contract.title} · ${t.line_item.item_name} · ${t.sub_item.sub_item_name}`;
    if (t.line_item) return `${t.contract.contract_number || t.contract.title} · ${t.line_item.item_name}`;
    return t.contract.contract_number || t.contract.title;
  }

  function fmt(d) {
    if (!d) return '';
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '';
    return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
  }

  /* ============================================================= */
  const S = styles;
  return (
    <div style={S.root}>
      {/* ── Filter bar ── */}
      <div style={S.filterBar}>
        <div style={S.filterGroup}>
          <div style={S.filterLabel}>Employee Filter (Column 1)</div>
          <div style={S.chipRow}>
            <input style={S.chipInput} placeholder="Name / Code..." value={lName} onChange={e => setLName(e.target.value)} />
            <select style={S.chipInput} value={lDept} onChange={e => setLDept(e.target.value)}>
              <option value="">All Departments</option>
              {departments.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <select style={S.chipInput} value={lGroup} onChange={e => setLGroup(e.target.value)}>
              <option value="">All Skill Groups</option>
              {skillGroups.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
            </select>
            <select style={S.chipInput} value={lSkillset} onChange={e => setLSkillset(e.target.value)}>
              <option value="">All Skillsets</option>
              {skillsets.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <select style={S.chipInput} value={showFilter} onChange={e => setShowFilter(e.target.value)} title="Show">
              <option value="all">All</option>
              <option value="noalloc">No Allocation</option>
              <option value="alloc">Has Allocation</option>
            </select>
          </div>
        </div>
        <div style={S.filterGroup}>
          <div style={S.filterLabel}>Contract Filter (Column 3)</div>
          <div style={S.chipRow}>
            <input style={S.chipInput} placeholder="Search contract / customer..." value={rSearch} onChange={e => setRSearch(e.target.value)} />
            <select style={S.chipInput} value={rStatus} onChange={e => setRStatus(e.target.value)}>
              <option value="All">All Statuses</option>
              <option value="Draft">Draft</option>
              <option value="Active">Active</option>
              <option value="Closed">Closed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
            <select style={S.chipInput} value={rCustomer} onChange={e => setRCustomer(e.target.value)}>
              <option value="">All Customers</option>
              {customers.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* ── Legend ── */}
      <div style={S.legend}>
        <span><span style={{ ...S.legendDot, background: '#00E0FF' }} />Active Allocation</span>
        <span style={{ marginLeft: 'auto', color: '#64748B', fontSize: 11 }}>
          Drag an employee from the left onto a contract, line item, or sub-item on the right. Saves instantly.
        </span>
      </div>

      {/* ── Spatial canvas ── */}
      <div ref={canvasRef} style={S.canvas}>
        <svg ref={svgRef} style={S.svg} xmlns="http://www.w3.org/2000/svg"></svg>

        <div style={S.centralLabel}>Drag across the canvas to allocate</div>

        {/* Left rail */}
        <div ref={leftRef} style={{ ...S.rail, left: 16 }}>
          <div style={S.railTitle}>
            <span>Employees</span>
            <span style={{ color: '#64748B' }}>
              {leftList.length === totalActiveEmployees
                ? leftList.length
                : `${leftList.length} of ${totalActiveEmployees}`}
            </span>
          </div>
          {leftList.length === 0 ? (
            <div style={S.empty}>No employees match the filters</div>
          ) : leftList.map(e => {
            const myAllocs = allocByEmp[e.id] || [];
            return (
              <div
                key={e.id}
                data-role="emp-left"
                data-id={e.id}
                draggable
                onDragStart={ev => { ev.dataTransfer.setData('text/eid', e.id); setDraggingEmpId(e.id); }}
                onDragEnd={() => setDraggingEmpId(null)}
                style={{
                  ...S.node,
                  opacity: draggingEmpId === e.id ? 0.35 : 1,
                  cursor: 'grab',
                }}
              >
                <EmpBubble emp={e} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={S.nodeName}>{e.first_name} {e.last_name}</div>
                  <div style={S.nodeSub}>{e.emp_code} · {e.department_name || '—'} · {e.module || '—'}</div>
                  {myAllocs.length > 0 && (
                    <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }} onMouseDown={ev => ev.stopPropagation()}>
                      {myAllocs.slice(0, 3).map(a => (
                        <span
                          key={a.id}
                          title={`${a.contract_number || ''} ${a.line_item_name ? '· ' + a.line_item_name : ''} ${a.sub_item_name ? '· ' + a.sub_item_name : ''} — click × to remove`}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 2,
                            fontSize: 9.5, fontWeight: 700, padding: '1px 3px 1px 5px',
                            borderRadius: 3, lineHeight: 1.4,
                            color: '#00E0FF',
                            background: '#00E0FF22',
                            border: '1px solid #00E0FF55',
                          }}>
                          {a.contract_number || (a.contract_title || '').slice(0, 10)}
                          <button
                            onClick={async (ev) => {
                              ev.stopPropagation();
                              const ok = await toast.confirm({
                                title: 'Remove allocation?',
                                body: `${e.first_name} ${e.last_name} will be un-allocated from ${describeTargetForAlloc(a)}.`,
                                danger: true, confirmLabel: 'Remove',
                              });
                              if (ok) removeAllocation(a.id);
                            }}
                            style={{
                              all: 'unset', cursor: 'pointer',
                              width: 12, height: 12, borderRadius: 6,
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              marginLeft: 2, fontSize: 10, lineHeight: 1,
                              color: '#00E0FF', opacity: 0.85,
                            }}
                            onMouseEnter={(ev) => ev.currentTarget.style.background = '#00E0FF33'}
                            onMouseLeave={(ev) => ev.currentTarget.style.background = 'transparent'}
                            title="Remove this allocation"
                          >×</button>
                        </span>
                      ))}
                      {myAllocs.length > 3 && (
                        <span style={{ fontSize: 9.5, color: '#94A3B8' }}>+{myAllocs.length - 3}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Right rail — hierarchical contract tree */}
        <div ref={rightRef} style={{ ...S.rail, right: 16, width: 360 }}>
          <div style={S.railTitle}>
            <span>Customer Contracts</span>
            <span style={{ color: '#64748B' }}>{filteredTree.length}</span>
          </div>
          {filteredTree.length === 0 ? (
            <div style={S.empty}>No contracts match the filters</div>
          ) : filteredTree.map(c => {
            const contractKey = `contract:${c.id}`;
            const hasChildren = (c.line_items || []).length > 0;
            const isExpanded = expanded[contractKey] !== false; // default expanded
            return (
              <div key={c.id} style={{ marginBottom: 8 }}>
                {/* Contract header — also a drop target */}
                <div
                  data-role="tgt"
                  data-key={contractKey}
                  onDragOver={ev => { ev.preventDefault(); setHoverTargetKey(contractKey); }}
                  onDragLeave={() => setHoverTargetKey(null)}
                  onDrop={ev => {
                    ev.preventDefault();
                    const eid = ev.dataTransfer.getData('text/eid');
                    setHoverTargetKey(null);
                    if (!eid) return;
                    handleDrop(eid, { contract: c });
                  }}
                  onClick={() => hasChildren && toggleExpand(contractKey)}
                  style={{
                    ...S.contractHeader,
                    cursor: hasChildren ? 'pointer' : 'default',
                    borderColor: hoverTargetKey === contractKey ? '#FF00E5' : 'rgba(0,224,255,0.35)',
                    background: hoverTargetKey === contractKey ? 'rgba(255,0,229,0.12)' : 'rgba(0,224,255,0.06)',
                    boxShadow: hoverTargetKey === contractKey ? '0 0 24px rgba(255,0,229,0.25)' : 'none',
                  }}
                >
                  <span style={{ width: 14, color: '#00E0FF', fontSize: 10 }}>
                    {hasChildren ? (isExpanded ? '▼' : '▶') : '·'}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span style={S.contractCode}>{c.contract_number || '—'}</span>
                      <span style={{
                        fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 3,
                        background: String(c.status || '').toLowerCase() === 'active'
                          ? 'rgba(16,185,129,0.18)' : 'rgba(148,163,184,0.15)',
                        color: String(c.status || '').toLowerCase() === 'active' ? '#10B981' : '#94A3B8',
                      }}>{c.status || 'Draft'}</span>
                    </div>
                    <div style={{ ...S.nodeName, marginTop: 2 }}>{c.title || '(Untitled contract)'}</div>
                    <div style={S.nodeSub}>
                      {c.customer_name || '—'}
                      {c.project_name && <span> · {c.project_name}</span>}
                      {(c.start_date || c.end_date) && <span> · {fmt(c.start_date)}–{fmt(c.end_date)}</span>}
                    </div>
                  </div>
                </div>

                {/* Line items */}
                {isExpanded && (c.line_items || []).map(li => {
                  const itemKey = `item:${li.id}`;
                  const hasSubs = (li.sub_items || []).length > 0;
                  const itemExpanded = expanded[itemKey] !== false;
                  return (
                    <div key={li.id} style={{ marginLeft: 16, marginTop: 4 }}>
                      <div
                        data-role="tgt"
                        data-key={itemKey}
                        onDragOver={ev => { ev.preventDefault(); setHoverTargetKey(itemKey); }}
                        onDragLeave={() => setHoverTargetKey(null)}
                        onDrop={ev => {
                          ev.preventDefault();
                          const eid = ev.dataTransfer.getData('text/eid');
                          setHoverTargetKey(null);
                          if (!eid) return;
                          handleDrop(eid, { contract: c, line_item: li });
                        }}
                        onClick={() => hasSubs && toggleExpand(itemKey)}
                        style={{
                          ...S.itemNode,
                          cursor: hasSubs ? 'pointer' : 'default',
                          borderColor: hoverTargetKey === itemKey ? '#FF00E5' : 'rgba(191,95,255,0.28)',
                          background: hoverTargetKey === itemKey ? 'rgba(255,0,229,0.12)' : 'rgba(191,95,255,0.05)',
                          boxShadow: hoverTargetKey === itemKey ? '0 0 18px rgba(255,0,229,0.22)' : 'none',
                        }}
                      >
                        <span style={{ width: 12, color: '#BF5FFF', fontSize: 9 }}>
                          {hasSubs ? (itemExpanded ? '▼' : '▶') : '·'}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={S.itemName}>
                            {li.seq ? `${li.seq}. ` : ''}{li.item_name || li.product_name || '(Line item)'}
                          </div>
                          <div style={S.itemSub}>
                            {li.product_name && <span>{li.product_name}</span>}
                            {(li.start_date || li.end_date) && <span> · {fmt(li.start_date)}–{fmt(li.end_date)}</span>}
                            {li.quantity != null && <span> · Qty {li.quantity}</span>}
                          </div>
                        </div>
                      </div>

                      {/* Sub-items */}
                      {itemExpanded && (li.sub_items || []).map(si => {
                        const subKey = `sub:${si.id}`;
                        return (
                          <div
                            key={si.id}
                            data-role="tgt"
                            data-key={subKey}
                            onDragOver={ev => { ev.preventDefault(); setHoverTargetKey(subKey); }}
                            onDragLeave={() => setHoverTargetKey(null)}
                            onDrop={ev => {
                              ev.preventDefault();
                              const eid = ev.dataTransfer.getData('text/eid');
                              setHoverTargetKey(null);
                              if (!eid) return;
                              handleDrop(eid, { contract: c, line_item: li, sub_item: si });
                            }}
                            style={{
                              ...S.subNode,
                              marginLeft: 16,
                              borderColor: hoverTargetKey === subKey ? '#FF00E5' : 'rgba(59,143,244,0.28)',
                              background: hoverTargetKey === subKey ? 'rgba(255,0,229,0.12)' : 'rgba(59,143,244,0.05)',
                              boxShadow: hoverTargetKey === subKey ? '0 0 14px rgba(255,0,229,0.22)' : 'none',
                            }}
                          >
                            <span style={{ width: 10, color: '#3B8FF4', fontSize: 9 }}>·</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={S.subName}>
                                {si.seq ? `${si.seq}. ` : ''}{si.sub_item_name || si.skillset_name || '(Sub-item)'}
                              </div>
                              <div style={S.subSub}>
                                {si.skillset_name && <span>{si.skillset_name}</span>}
                                {si.employee_name && <span> · {si.employee_name}</span>}
                                {si.allocation_pct != null && <span> · {si.allocation_pct}%</span>}
                                {(si.start_date || si.end_date) && <span> · {fmt(si.start_date)}–{fmt(si.end_date)}</span>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        <div style={S.badges}>
          <span style={S.pill}><span style={{ ...S.dot, background: '#00E0FF' }} />Contract</span>
          <span style={S.pill}><span style={{ ...S.dot, background: '#BF5FFF' }} />Line Item</span>
          <span style={S.pill}><span style={{ ...S.dot, background: '#3B8FF4' }} />Sub-Item</span>
        </div>
      </div>
    </div>
  );
}

function describeTargetForAlloc(a) {
  const bits = [a.contract_number || a.contract_title || 'contract'];
  if (a.line_item_name) bits.push(a.line_item_name);
  if (a.sub_item_name) bits.push(a.sub_item_name);
  return bits.join(' · ');
}

/* ========== inline emp avatar ========== */
function EmpBubble({ emp }) {
  if (emp.photo_path) {
    return (
      <img
        src={emp.photo_path}
        alt=""
        style={{
          width: 32, height: 32, borderRadius: '50%', objectFit: 'cover',
          border: '1px solid rgba(255,255,255,0.12)', flexShrink: 0,
        }}
      />
    );
  }
  const initials = ((emp.first_name || '')[0] || '') + ((emp.last_name || '')[0] || '');
  let h = 0;
  const seed = `${emp.first_name || ''} ${emp.last_name || ''}`;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  const h1 = h % 360, h2 = (h1 + 40) % 360;
  return (
    <div style={{
      width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 11, fontWeight: 700, color: '#fff',
      background: `linear-gradient(135deg, hsl(${h1} 70% 55%), hsl(${h2} 70% 45%))`,
    }}>{initials || '?'}</div>
  );
}

/* ========== styles ========== */
const styles = {
  root: { display: 'flex', flexDirection: 'column', gap: 14 },
  filterBar: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 10, padding: '14px 16px',
    display: 'grid', gap: 14,
    gridTemplateColumns: '1fr 1fr',
    alignItems: 'end',
  },
  filterGroup: { minWidth: 0 },
  filterLabel: {
    fontSize: 11, color: '#94A3B8', textTransform: 'uppercase',
    letterSpacing: '0.5px', fontWeight: 600, marginBottom: 8,
  },
  chipRow: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  chipInput: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 6, padding: '6px 10px',
    fontSize: 12.5, color: '#E5E7EB', minWidth: 110, outline: 'none',
  },
  legend: {
    display: 'flex', gap: 16, alignItems: 'center',
    fontSize: 11.5, color: '#94A3B8', padding: '2px 4px',
  },
  legendDot: {
    display: 'inline-block', width: 10, height: 3, borderRadius: 2,
    marginRight: 6, verticalAlign: 'middle',
  },
  canvas: {
    border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12,
    position: 'relative', overflow: 'hidden', minHeight: 680,
    background: `
      radial-gradient(circle at 20% 30%, rgba(0,224,255,0.06), transparent 40%),
      radial-gradient(circle at 80% 70%, rgba(191,95,255,0.06), transparent 40%),
      rgba(255,255,255,0.03)`,
    backgroundImage: `
      linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)`,
    backgroundSize: '24px 24px',
  },
  svg: { position: 'absolute', inset: 0, pointerEvents: 'none', width: '100%', height: '100%' },
  centralLabel: {
    position: 'absolute', top: 16, left: '50%', transform: 'translateX(-50%)',
    fontSize: 10.5, color: '#94A3B8', letterSpacing: '0.6px',
    textTransform: 'uppercase', fontWeight: 600,
  },
  rail: {
    position: 'absolute', top: 16, bottom: 16, width: 300, padding: 12,
    overflowY: 'auto', background: 'rgba(10,14,24,0.92)',
    border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10,
    backdropFilter: 'blur(8px)',
  },
  railTitle: {
    fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.7px',
    color: '#94A3B8', marginBottom: 8, fontWeight: 700,
    display: 'flex', justifyContent: 'space-between',
  },
  node: {
    display: 'flex', alignItems: 'center', gap: 9, padding: '8px 10px',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 10, marginBottom: 6,
    transition: 'all 0.15s ease',
  },
  nodeName: {
    fontSize: 12, fontWeight: 600, color: '#E5E7EB',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  nodeSub: { fontSize: 10, color: '#64748B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  contractHeader: {
    display: 'flex', alignItems: 'flex-start', gap: 8, padding: '9px 10px',
    border: '1px solid rgba(0,224,255,0.35)',
    borderRadius: 10,
    transition: 'all 0.15s ease',
  },
  contractCode: {
    fontSize: 10, fontWeight: 700, letterSpacing: '0.3px',
    color: '#00E0FF', fontFamily: 'ui-monospace, SFMono-Regular, monospace',
  },
  itemNode: {
    display: 'flex', alignItems: 'flex-start', gap: 7, padding: '6px 9px',
    border: '1px solid rgba(191,95,255,0.28)',
    borderRadius: 8, marginTop: 3,
    transition: 'all 0.15s ease',
  },
  itemName: {
    fontSize: 11.5, fontWeight: 600, color: '#E5E7EB',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  itemSub: { fontSize: 9.5, color: '#64748B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  subNode: {
    display: 'flex', alignItems: 'flex-start', gap: 6, padding: '5px 8px',
    border: '1px solid rgba(59,143,244,0.28)',
    borderRadius: 7, marginTop: 3,
    transition: 'all 0.15s ease',
  },
  subName: {
    fontSize: 11, fontWeight: 500, color: '#CBD5E1',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  subSub: { fontSize: 9.5, color: '#64748B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  badges: {
    position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)',
    display: 'flex', gap: 10, padding: '8px 14px',
    background: 'rgba(0,0,0,0.55)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 30, backdropFilter: 'blur(6px)', fontSize: 11, color: '#E5E7EB',
  },
  pill: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '4px 10px', borderRadius: 20, background: 'rgba(255,255,255,0.04)',
  },
  dot: { width: 8, height: 8, borderRadius: '50%' },
  empty: { padding: 20, textAlign: 'center', color: '#64748B', fontSize: 12 },
};
